import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { ownShownIdentity, type OwnShownIdentity } from 'lib/community/model'
import { draftPreview, type OwnerNoteDraft } from 'lib/community/review'
import { sharedMe } from 'lib/owner-notes/api'
import type { CommunityTier } from 'lib/community/types'
import type { OwnerQuestionSetDef } from 'lib/dealhunt/types'
import MemberName from './MemberName'
import TierAvatar from './TierAvatar'
import base from './community.module.css'
import feed from './Feed.module.css'
import styles from './Review.module.css'

interface ReviewPreviewProps {
  draft: OwnerNoteDraft
  /** The set the band loaded — the labels the feed will print. null while loading. */
  questions: OwnerQuestionSetDef | null
  productName: string
  /** The reader's pseudonym from `/me`; null before a device exists. */
  handle: string | null
  tier: CommunityTier | null
  /** A footer under the row, in the card's fine print (the band's sign-in gate lead, 2026-10-17).
   *  None from the community composer. */
  footer?: React.ReactNode
  /** Every photo the reader has picked, in order (local previews or DealHunt URLs), held,
   *  uploading or done — the strip the feed will print (three thumbs, "+N" over the last).
   *  Client state only, never in server HTML. Absent: the single-cover slot from `draft`. */
  photos?: readonly { src: string }[]
}

/** On a phone the preview reads like the phone feed card: the sentence clamped to four lines
 *  (CSS) and this many chips, then "+N". */
export const PREVIEW_PHONE_CHIPS = 2

/**
 * "How it appears in the community" (design 5b): the draft as a feed row, updated as the reader
 * answers. Its own markup over `Feed.module.css`, not `FeedRow`: a draft has no id, no date and
 * no page to link to, and a row with `data-note-id` would be a note the views beacon could
 * count. The chips follow the feed's rules (`draftPreview` → `readChips` / `buyAgainChip`).
 */
/** The feed shows at most this many thumbs; the rest is a "+N" over the last one. */
export const PREVIEW_PHOTO_THUMBS = 3

const ReviewPreview: React.VFC<ReviewPreviewProps> = ({
  draft,
  questions,
  productName,
  handle,
  tier,
  footer = null,
  photos,
}) => {
  const preview = draftPreview(draft, questions)
  // The reader's own name and photo, as the community will show them (contract B.3). Read from
  // the page's memoised `/me` (the composer already read it — a cache hit), only once a device
  // exists (`handle` comes from that `/me`); the preview is client state, never cached HTML.
  const [shown, setShown] = useState<OwnShownIdentity | null>(null)
  useEffect(() => {
    if (handle === null) {
      setShown(null)
      return undefined
    }
    let live = true
    sharedMe().then(
      me => {
        if (live) {
          setShown(ownShownIdentity(me))
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [handle])
  const hiddenOnPhone = Math.max(
    0,
    preview.chips.length + (preview.again ? 1 : 0) - PREVIEW_PHONE_CHIPS
  )
  return (
    <section className={base.card} aria-labelledby="review-preview-title">
      <p id="review-preview-title" className={base.eyebrow}>
        {'How it appears in the community'}
      </p>
      <div className={styles.previewRow}>
        <p className={feed.meta}>
          <span className={base.kind}>{'Owner review'}</span>
          <span className={feed.product}>{productName}</span>
          {/* The dot leads the author group, so a wrapped row never ends on a dangling "·". */}
          <span className={styles.previewAuthor}>
            <span className={feed.dot} aria-hidden="true">
              {'·'}
            </span>
            <TierAvatar
              handle={handle ?? 'you'}
              tier={tier}
              size="xs"
              src={shown?.avatarUrl}
              name={shown?.name}
            />
            <span className={feed.authorPlain}>
              {handle ? <MemberName name={shown?.name} handle={handle} /> : 'your pseudonym'}
            </span>
          </span>
          {preview.ownership && (
            <span className={base.ownerPill}>{`Owner · ${preview.ownership}`}</span>
          )}
        </p>
        <blockquote
          className={clsx(feed.quote, styles.previewQuote, {
            [styles.placeholder]: !preview.sentence,
          })}
        >
          {preview.sentence ? `“${preview.sentence}”` : 'Your sentence shows here.'}
        </blockquote>
        {photos && photos.length > 0 ? (
          // The strip as the feed prints it: up to three 4:3 thumbs, "+N" over the last.
          <span className={styles.previewStrip} data-testid="preview-photos">
            {photos.slice(0, PREVIEW_PHOTO_THUMBS).map((photo, i) => (
              <span key={photo.src} className={styles.previewThumb}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={photo.src} alt="" />
                {i === PREVIEW_PHOTO_THUMBS - 1 && photos.length > PREVIEW_PHOTO_THUMBS && (
                  <span className={styles.previewPhotoMore}>
                    {`+${photos.length - PREVIEW_PHOTO_THUMBS}`}
                  </span>
                )}
              </span>
            ))}
          </span>
        ) : null}
        {!photos?.length && draft.cover && (
          // The first photo, as the feed's strip will lead with it (a local preview while the
          // reader is still writing — never in the server HTML: the draft is client state).
          <span className={styles.previewPhoto}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={draft.cover} alt="" width={64} height={48} />
            {draft.photoCount > 1 && (
              <span className={styles.previewPhotoMore}>{`+${draft.photoCount - 1}`}</span>
            )}
          </span>
        )}
        {(preview.chips.length > 0 || preview.again) && (
          <ul className={clsx(feed.chips, styles.previewChips)}>
            {preview.chips.map(chip => (
              <li key={chip.id} className={clsx(feed.chip, feed[`chip_${chip.tone}`])}>
                {chip.text}
              </li>
            ))}
            {preview.again && (
              <li
                className={clsx(
                  feed.chip,
                  preview.again.yes ? feed.chip_again : feed.chip_notAgain
                )}
              >
                {preview.again.text}
              </li>
            )}
            {hiddenOnPhone > 0 && (
              <li className={clsx(feed.chip, styles.previewMore)}>{`+${hiddenOnPhone}`}</li>
            )}
          </ul>
        )}
      </div>
      {footer && <div className={base.fine}>{footer}</div>}
    </section>
  )
}

export default ReviewPreview
